<template>
	<div>
		<form id="payment-form">
			<div
				class="digital-wallet"
				v-show="order.Balance > 0 && !enableBalanceCheckout"
			>
				<div
					id="apple-pay-button"
					v-if="macOS && !client.DisableApplePayOnline"
					@click="handleDigitalWalletPayment($event, applePay, 1)"
				></div>
				<div
					id="google-pay-button"
					v-if="!client.DisableGooglePayOnline"
					@click="handleDigitalWalletPayment($event, googlePay, 2)"
				></div>
			</div>
			<div v-show="order.Balance != 0 && !enableBalanceCheckout">
				<div id="name-country" v-show="showNameCountry">
					<input
						type="text"
						id="billing-name"
						placeholder="Name on card"
						v-model="billingName"
						autocomplete="false"
						spellcheck="false"
						:class="{ 'field-error': nameError }"
						@input="nameError = billingName.trim().length == 0"
					/>
					<select
						id="country-select"
						v-model="billingCountry"
						:class="{ 'field-error': countryError }"
						@change="countryError = billingCountry == null"
					>
						<option disabled selected value="null">Country/Region</option>
						<option
							v-if="firstCountryOption"
							:value="firstCountryOption.CountryCode"
							>{{ firstCountryOption.Name }}</option
						>
						<option :value="usCountryOption.CountryCode">{{
							usCountryOption.Name
						}}</option>
						<option
							v-for="country in client.Countries"
							:key="country.CountryKey"
							:value="country.CountryCode"
							>{{ country.Name }}</option
						>
					</select>
				</div>
				<div id="card-container"></div>
				<p class="text_size-medium-small">
					All transactions are secure and encrypted.
				</p>
			</div>
			<button
				id="card-button"
				type="button"
				class="button blue text-center wide-cart-btn"
				@click="handlePaymentMethodSubmission($event, card)"
			>
				Complete Order
			</button>
		</form>
		<div id="payment-status-container"></div>
	</div>
</template>

<script>
	export default {
		props: ['client', 'order'],
		data() {
			return {
				card: null,
				googlePay: null,
				applePay: null,
				appId: '',
				locationId: '',
				showNameCountry: false,
				billingCountry: null,
				billingName: '',
				nameError: false,
				countryError: false,
			};
		},
		mounted() {
			this.appId = this.client.PaymentPublicApiKey;
			this.locationId = this.client.SquareLocationId;
			this.initSquareWebPayments();
		},
		computed: {
			macOS() {
				return (
					/iPad|iPhone|iPod/.test(navigator.userAgent) ||
					/Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.platform)
				);
			},
			firstCountryOption() {
				if (this.client.CountryKey == 196) return null;
				return this.client.Countries.find(
					(c) => c.CountryKey == this.client.CountryKey
				);
			},
			usCountryOption() {
				return this.client.Countries.find((c) => c.CountryKey == 196);
			},
			enableBalanceCheckout() {
				try {
					return this.order.EnableBalanceCheckout;
				} catch {
					return false;
				}
			},
		},
		methods: {
			async initSquareWebPayments() {
				const payments = window.Square.payments(this.appId, this.locationId);

				try {
					this.card = await this.initCard(payments).finally(
						() => (this.showNameCountry = true)
					);
				} catch (e) {
					console.error('Initializing Card failed', e);
				}

				if (!this.client.DisableGooglePayOnline) {
					try {
						this.googlePay = await this.initGooglePay(payments);
					} catch (e) {
						console.error('Initializing Google Pay failed', e);
					}
				}

				if (this.macOS && !this.client.DisableApplePayOnline) {
					try {
						this.applePay = await this.initApplePay(payments);
					} catch (e) {
						console.error('Initializing Apple Pay failed', e);
					}
				}
			},
			async initCard(payments) {
				const card = await payments.card();
				await card.attach('#card-container');
				return card;
			},
			async initGooglePay(payments) {
				const paymentRequest = this.buildPaymentRequest(payments);

				const googlePay = await payments.googlePay(paymentRequest);
				await googlePay.attach('#google-pay-button');
				return googlePay;
			},
			async initApplePay(payments) {
				const paymentRequest = this.buildPaymentRequest(payments);
				const applePay = await payments.applePay(paymentRequest);
				return applePay;
			},
			async handleDigitalWalletPayment(
				event,
				paymentMethod,
				digitalWalletType
			) {
				await this.handlePaymentMethodSubmission(
					event,
					paymentMethod,
					digitalWalletType
				);
			},
			async handlePaymentMethodSubmission(event, paymentMethod, digitalWallet) {
				event.preventDefault();
				try {
					if (!this.enableBalanceCheckout && this.order.Balance > 0) {
						this.nameError = this.billingName.trim().length == 0;
						this.countryError = this.billingCountry == null;

						if (!this.nameError && !this.countryError)
							await paymentMethod.tokenize().then((token) => {
								if (!token.errors && token.status != 'Cancel')
									this.$emit('submitPayment', {
										...token,
										digitalWallet,
										billingName: this.billingName,
										billingCountry: this.billingCountry,
									});
							});
					} else this.$emit('submitPayment', null);
				} catch (e) {
					console.error(e.message);
				}
			},
			buildPaymentRequest(payments) {
				return payments.paymentRequest({
					countryCode: this.client.CountryCode.toUpperCase(),
					currencyCode: this.client.SquareCurrencyCode,
					total: {
						amount: this.order.Total.toString(),
						label: this.client.Name,
					},
				});
			},
		},
	};
</script>
<style lang="scss">
	.digital-wallet {
		display: flex;
		flex-direction: row;
		width: 100%;
	}
	#google-pay-button {
		height: 48px;
		width: 100%;
		display: inline-block;
	}
	#apple-pay-button {
		height: 40px;
		width: 100%;
		display: inline-block;
		-webkit-appearance: -apple-pay-button;
		-apple-pay-button-type: plain;
		-apple-pay-button-style: black;
		cursor: pointer !important;
	}
	#payment-form {
		max-width: 100%;
		min-width: 100%;
	}
	label {
		font-size: 12px;
		width: 100%;
	}

	#name-country {
		width: 100%;
		margin-bottom: 8px;
		display: flex;
		flex-direction: row;
		gap: 6px;

		input,
		select {
			border: 1px solid #ddd;
			border-radius: 6px;
			margin-bottom: 8px;
			min-height: 48px;
			font-size: 16px;
			margin: 0;
			padding: 0 16px;
			height: inherit;
			max-width: 50%;
			flex: 1 0 auto;

			&::placeholder {
				color: #525456 !important;
				opacity: 0.7;
			}

			&:focus {
				border: 2px solid blue;
			}

			&.field-error {
				border: 2px solid #cc0023;
			}
		}

		select {
			-webkit-appearance: none;
			-moz-appearance: none;
			text-indent: 1px;
			text-overflow: '';
			position: relative;

			-webkit-appearance: none;
			-moz-appearance: none;
			background: transparent;
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='icon icon-tabler icon-tabler-chevron-down' width='24' height='24' viewBox='0 0 24 24' stroke-width='2' stroke='currentColor' fill='none' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath stroke='none' d='M0 0h24v24H0z' fill='none'%3E%3C/path%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
			background-repeat: no-repeat;
			background-position-x: 96%;
			background-position-y: 45%;

			&:first-child {
				color: #525456 !important;
				opacity: 0.7;
			}
		}

		@media only screen and (max-width: 942px) {
			height: fit-content;
			flex-wrap: wrap;

			input,
			select {
				max-width: 100%;
				min-width: 100%;
			}

			input {
				min-width: calc(100% - 16px * 2 - 2px);
			}
		}
	}
</style>
